import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { ASSET_TYPES, ENTITY_TYPES } from '@/shared/api/openmetadata';
import { mapTagsToBasicInfo } from '@/shared/lib/tag';
import { getTagRoute } from '@/shared/lib/tags';
import { buildServiceUrl } from '@/shared/lib/url/serviceUrl';
import { formatBytes } from '@/shared/lib/utils';
import {
  DrawerDatabaseSchemaList,
  DrawerDomainList,
  DrawerEmptyValue,
  DrawerOwnerList,
  DrawerSchemaList,
} from '@/shared/ui/AssetMetadata';
import { BadgeList } from '@/shared/ui/BadgeList';
import type { DrawerSection, InfoRow } from '@/shared/ui/lineage/LineageNodeDrawer';
import { TierDisplay } from '@/shared/ui/TierDisplay';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { filterEntityLabels } from '@/entities/search';
import type { OpenMetadataSource, SearchResult } from '@/entities/search';

import { useAssetBaseInfo } from '@/features/asset-detail';
import { useColumnData } from '@/features/asset-detail/model/useColumnData';

type UseOpenMetadataPreviewProps = {
  selectedResult: SearchResult | null;
  sourceData?: OpenMetadataSource;
};

export const useOpenMetadataPreview = ({ selectedResult, sourceData }: UseOpenMetadataPreviewProps) => {
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);
  const navigate = useNavigate();

  const fqn = selectedResult?.path || '';
  const isRun = selectedResult?.type === 'run';
  const shouldFetch = !!selectedResult && !!fqn && !sourceData && !isRun;

  const {
    data: assetData,
    isLoading: isAssetLoading,
    isError: isAssetError,
    refetch,
  } = useAssetBaseInfo(selectedResult?.type || '', fqn, shouldFetch);

  const dataToUse = sourceData || assetData;

  const shouldFetchColumns = selectedResult?.type === 'table' && !!fqn;
  const { data: columnResponse, isLoading: isColumnsLoading } = useColumnData(fqn, shouldFetchColumns);

  const columns = useMemo(() => columnResponse?.data || [], [columnResponse?.data]);

  const infoRows = useMemo((): InfoRow[] => {
    if (!selectedResult || !dataToUse || isRun) return [];

    const entityType = selectedResult.type;

    if (entityType === ASSET_TYPES.DATABASE) {
      return [
        {
          label: '계층',
          value: dataToUse.tier ? <TierDisplay tierName={dataToUse.tier.name} formatName={false} /> : '-',
        },
        {
          label: '서비스',
          value: dataToUse.service?.displayName || dataToUse.service?.name || '-',
          linkTo:
            buildServiceUrl({
              serviceType: dataToUse.service?.type,
              serviceName: dataToUse.service?.name,
            }) || undefined,
        },
        {
          label: '사용량',
          value: dataToUse.usageSummary?.weeklyStats?.percentileRank
            ? `${dataToUse.usageSummary.weeklyStats.percentileRank}%`
            : '0%',
        },
      ];
    }

    if (entityType === ASSET_TYPES.DATABASE_SCHEMA) {
      return [
        {
          label: '계층',
          value: dataToUse.tier ? <TierDisplay tierName={dataToUse.tier.name} /> : '-',
        },
        {
          label: '서비스',
          value: dataToUse.service?.displayName || dataToUse.service?.name || '-',
          linkTo:
            buildServiceUrl({
              serviceType: dataToUse.service?.type,
              serviceName: dataToUse.service?.name,
            }) || undefined,
        },
        {
          label: '데이터베이스',
          value: dataToUse.database?.displayName || dataToUse.database?.name || '-',
          linkTo: dataToUse.database?.fullyQualifiedName
            ? `/database/${dataToUse.database.fullyQualifiedName}`
            : undefined,
        },
        {
          label: '사용량',
          value: dataToUse.usageSummary?.weeklyStats?.percentileRank
            ? `${dataToUse.usageSummary.weeklyStats.percentileRank}%`
            : '0%',
        },
      ];
    }

    if (entityType === ASSET_TYPES.MLMODEL) {
      return [
        { label: '알고리즘', value: <DrawerEmptyValue>-</DrawerEmptyValue> },
        { label: '대상', value: <DrawerEmptyValue>-</DrawerEmptyValue> },
        { label: '서버', value: <DrawerEmptyValue>-</DrawerEmptyValue> },
        { label: '대시보드', value: <DrawerEmptyValue>-</DrawerEmptyValue> },
      ];
    }

    if (entityType === ASSET_TYPES.CONTAINER) {
      return [
        {
          label: '객체',
          value: dataToUse.numberOfObjects !== undefined ? dataToUse.numberOfObjects : '-',
        },
        {
          label: '서비스 유형',
          value: selectedResult.serviceType || <DrawerEmptyValue>-</DrawerEmptyValue>,
        },
        { label: '컬럼', value: <DrawerEmptyValue>-</DrawerEmptyValue> },
      ];
    }

    if (entityType === ASSET_TYPES.TOPIC) {
      return [
        {
          label: '파티션',
          value: dataToUse.partitions !== undefined ? dataToUse.partitions : '-',
        },
        {
          label: '복제 계수',
          value: dataToUse.replicationFactor !== undefined ? dataToUse.replicationFactor : '-',
        },
        { label: '보존 크기', value: 'N/A' },
        {
          label: '정리 정책',
          value:
            dataToUse.cleanupPolicies && dataToUse.cleanupPolicies.length > 0
              ? dataToUse.cleanupPolicies.join(', ')
              : '-',
        },
        {
          label: '최대 메시지 크기',
          value: formatBytes(dataToUse.maximumMessageSize),
        },
        {
          label: '스키마 유형',
          value: dataToUse.messageSchema?.schemaType || '-',
        },
      ];
    }

    if (entityType === ASSET_TYPES.EXPERIMENT) {
      return [];
    }

    return [];
  }, [selectedResult, dataToUse, isRun]);

  const description = useMemo(() => {
    if (!selectedResult || !dataToUse || isRun) return null;
    return dataToUse.description || null;
  }, [selectedResult, dataToUse, isRun]);

  const sections = useMemo((): DrawerSection[] => {
    if (!selectedResult || !dataToUse || isRun) return [];

    const createCommonSections = (data: typeof dataToUse, entityType: string): DrawerSection[] => {
      const sections: DrawerSection[] = [];

      if (entityType === ASSET_TYPES.EXPERIMENT) {
        return sections;
      }

      if (showOwners) {
        sections.push({
          id: 'owners',
          title: '소유자',
          content:
            data.owners && data.owners.length > 0 ? (
              <DrawerOwnerList
                owners={data.owners.map((owner) => ({
                  id: owner.id,
                  name: owner.name,
                  displayName: owner.displayName || owner.name,
                  type: owner.type,
                }))}
              />
            ) : (
              <DrawerEmptyValue>소유자 없음</DrawerEmptyValue>
            ),
        });
      }

      sections.push({
        id: 'domains',
        title: '도메인',
        content:
          data.domains && data.domains.length > 0 ? (
            <DrawerDomainList
              domains={data.domains.map((d) => ({
                id: d.id,
                name: d.name,
                displayName: d.displayName || d.name,
                fullyQualifiedName: d.fullyQualifiedName,
              }))}
            />
          ) : (
            <DrawerEmptyValue>연결된 도메인이 없습니다</DrawerEmptyValue>
          ),
      });

      const classificationTags = mapTagsToBasicInfo(
        filterEntityLabels(data.tags, {
          includeOnlySource: 'Classification',
          requireConfirmed: true,
        })
      );
      const uniqueClassificationTags = classificationTags.filter(
        (tag, index, self) => index === self.findIndex((t) => t.tagFQN === tag.tagFQN)
      );

      const glossaryTerms = mapTagsToBasicInfo(
        filterEntityLabels(data.tags, {
          includeOnlySource: 'Glossary',
          requireConfirmed: true,
        })
      );
      const uniqueGlossaryTerms = glossaryTerms.filter(
        (tag, index, self) => index === self.findIndex((t) => t.tagFQN === tag.tagFQN)
      );

      sections.push({
        id: 'tags',
        title: '태그',
        content:
          uniqueClassificationTags.length > 0 ? (
            <BadgeList
              items={uniqueClassificationTags}
              onItemClick={(tagFQN) => navigate(getTagRoute(tagFQN))}
              iconName="tag"
              isEllipsis={false}
            />
          ) : (
            <DrawerEmptyValue>연결된 태그가 없습니다</DrawerEmptyValue>
          ),
      });

      sections.push({
        id: 'terms',
        title: '용어',
        content:
          uniqueGlossaryTerms.length > 0 ? (
            <BadgeList
              items={uniqueGlossaryTerms}
              onItemClick={(tagFQN) => {
                const parts = tagFQN.split('.');
                if (parts.length >= 2) {
                  const category = parts[0];
                  const termPath = parts.slice(1).join('.');
                  navigate(`/glossary/${category}/${termPath}/glossary_terms`);
                } else {
                  navigate(`/glossary/${tagFQN}`);
                }
              }}
              iconName="book"
              isEllipsis={false}
            />
          ) : (
            <DrawerEmptyValue>연결된 용어가 없습니다</DrawerEmptyValue>
          ),
      });

      return sections;
    };

    const entityType = selectedResult.type;

    if (entityType === ASSET_TYPES.DATABASE) {
      const result = createCommonSections(dataToUse, entityType);
      result.push({
        id: 'schema',
        title: '스키마',
        content:
          dataToUse.databaseSchemas && dataToUse.databaseSchemas.length > 0 ? (
            <DrawerDatabaseSchemaList schemas={dataToUse.databaseSchemas} />
          ) : (
            <DrawerEmptyValue>스키마 없음</DrawerEmptyValue>
          ),
      });
      return result;
    }

    if (entityType === ENTITY_TYPES.DATA_PRODUCT) {
      const result = createCommonSections(dataToUse, entityType);
      if (showOwners) {
        const dataWithExperts = dataToUse as OpenMetadataSource & {
          experts?: Array<{ id: string; name: string; displayName?: string; type: string }>;
        };
        const experts = dataWithExperts.experts;
        if (experts && experts.length > 0) {
          result.splice(1, 0, {
            id: 'experts',
            title: '전문가',
            content: (
              <DrawerOwnerList
                owners={experts.map((expert) => ({
                  id: expert.id,
                  name: expert.name,
                  displayName: expert.displayName || expert.name,
                  type: expert.type,
                }))}
              />
            ),
          });
        }
      }
      return result;
    }

    if (entityType === ASSET_TYPES.CONTAINER) {
      const result = createCommonSections(dataToUse, entityType);
      result.push({
        id: 'schema',
        title: '스키마',
        content: <DrawerEmptyValue>사용 가능한 데이터가 없습니다.</DrawerEmptyValue>,
      });
      return result;
    }

    const result = createCommonSections(dataToUse, entityType);
    if (entityType === ASSET_TYPES.MLMODEL) {
      result.push({
        id: 'features',
        title: '기능',
        content: <DrawerEmptyValue>사용 가능한 기능 데이터가 없습니다</DrawerEmptyValue>,
      });
    }
    if (columns.length > 0) {
      result.push({
        id: 'schema',
        title: '스키마',
        content: <DrawerSchemaList columns={columns} isLoading={isColumnsLoading} />,
      });
    }

    return result;
  }, [selectedResult, dataToUse, columns, isColumnsLoading, showOwners, navigate, isRun]);

  return {
    infoRows,
    sections,
    description,
    isLoading: isAssetLoading,
    isError: isAssetError,
    refetch,
  };
};
